import { useQuery } from '@tanstack/react-query'

import { organizationsDomainAvailabilityRetrieveOptions } from 'src/api/main'
import { useDebouncedValue } from 'src/hooks'

import { HANDLE_MIN_LENGTH } from './constants'

export type HandleAvailability =
  | 'idle'
  | 'short'
  | 'checking'
  | 'free'
  | 'taken'

const DEBOUNCE_MS = 300

/**
 * Live "is this workspace address free?" check.
 *
 * Asks the backend, the single authority on what can be claimed:
 * `GET /organizations/domain-availability/?domain_name=` answers whether the slug
 * is free. A reserved slug counts as taken — the user only needs to know it is
 * unavailable, not why.
 */
export const useHandleAvailability = (handle: string): HandleAvailability => {
  const debounced = useDebouncedValue(handle, DEBOUNCE_MS)
  const enabled = debounced.length >= HANDLE_MIN_LENGTH

  const { data, isFetching, isError } = useQuery({
    ...organizationsDomainAvailabilityRetrieveOptions({
      query: { domain_name: debounced }
    }),
    enabled,
    retry: false,
    staleTime: 30_000,
    gcTime: 30_000
  })

  // Empty is "not answered yet", not "wrong" — nothing to say about it. Below the
  // minimum gets called out rather than showing no verdict at all.
  if (handle.length === 0) return 'idle'
  if (handle.length < HANDLE_MIN_LENGTH) return 'short'
  // Mid-typing: the debounce has not caught up with what is on screen yet, so the
  // field stays blank rather than spinning or showing a verdict already moved past.
  if (!enabled || handle !== debounced) return 'idle'
  if (isFetching) return 'checking'
  // A failed check must not read as free — the backend is the authority, and
  // claiming availability it never confirmed would wave an unavailable slug through.
  if (isError || !data) return 'idle'

  return data.available ? 'free' : 'taken'
}
